import {
    Component,
    EventEmitter,
    Input,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import module from './filter.module';
import { debounceTime, take } from 'rxjs/operators';
import { FormControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { composeUrl } from 'app/core/shared/helper/url.helper';
import { Response, PaginateResponse } from 'app/model';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-table-filter',
    templateUrl: './filter.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: module,
    styleUrls: ['./filter.component.scss'],
})
export class FilterComponent {
    private subscriptions: Subscription = new Subscription();

    constructor(
        private _httpClient: HttpClient,
        private _formBuilder: UntypedFormBuilder
    ) { }

    options = {
        fields: [],
        platforms: [],
        wells: [],
        wellStrings: [],
    };

    @Input() searchForm: UntypedFormGroup;

    @Output() fieldResult: EventEmitter<any[]> = new EventEmitter<any[]>();
    @Output() platformResult: EventEmitter<any[]> = new EventEmitter<any[]>();
    @Output() wellResult: EventEmitter<any[]> = new EventEmitter<any[]>();
    @Output() stringResult: EventEmitter<any[]> = new EventEmitter<any[]>();

    @Output() searchTriggered = new EventEmitter<any>();

    ngOnChanges() {
        this.validate();
        this.searchForm.addControl('Field', new FormControl(null));
        this.searchForm.addControl('Platform', new FormControl(null));
        this.searchForm.addControl('Well', new FormControl(null));
        this.searchForm.addControl('String', new FormControl(null));

        this.subscribeAutoComplete();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    getProp(type: string) {
        const props = {
            fields: 'fieldLongName',
            platforms: 'platformLongName',
            wells: 'wellLongName',
            wellStrings: 'stringLongName',
        };

        return props[type];
    }

    validate() {
        const inputs = ['FieldName', 'PlatformName', 'WellName', 'StringName'];
        for (const key in inputs) {
            if (!(inputs[key] in this.searchForm.value))
                throw `${inputs[key]} missing in searchForm FormGroup`;
        }
    }

    subscribeAutoComplete() {
        const fieldSubscription = this.searchForm
            .get('FieldName')
            .valueChanges.subscribe(
                this.autocomplete.bind(this, 'fields', this.getProp('fields'))
            );
        const platformSubscription = this.searchForm
            .get('PlatformName')
            .valueChanges.subscribe(
                this.autocomplete.bind(
                    this,
                    'platforms',
                    this.getProp('platforms')
                )
            );
        const wellSubscription = this.searchForm
            .get('WellName')
            .valueChanges.subscribe(
                this.autocomplete.bind(this, 'wells', this.getProp('wells'))
            );
        const stringSubscription = this.searchForm
            .get('StringName')
            .valueChanges.subscribe(
                this.autocomplete.bind(
                    this,
                    'wellStrings',
                    this.getProp('wellStrings')
                )
            );

        this.subscriptions.add(fieldSubscription);
        this.subscriptions.add(platformSubscription);
        this.subscriptions.add(wellSubscription);
        this.subscriptions.add(stringSubscription);
    }

    autocomplete(type: string, attr: string, value: string) {
        if (!value) {
            // clear the searchFrom attribute
            if (attr == 'fieldLongName') {
                this.searchForm.get('Field').setValue('',{ emitEvent: false })
            }

            if (attr == 'platformLongName') {
                this.searchForm.get('Platform').setValue('',{ emitEvent: false })
            }

            if (attr == 'wellLongName') {
                this.searchForm.get('Well').setValue('',{ emitEvent: false })
            }

            if (attr == 'stringLongName') {
                this.searchForm.get('String').setValue('',{ emitEvent: false })
            }

            return
        }

        if (value.length < 3) return

        this.getSearch(attr)
            .pipe(debounceTime(300), take(1))
            .subscribe((res: any) => {
                this.options = res.data;
                this.onSelected(type, attr, value);
            });
    }

    getSearch(attribute) {
        let FieldName = ''
        let PlatformName = ''
        let WellName = ''
        let StringName = ''

        if (attribute == 'fieldLongName') {
            FieldName = this.searchForm.get('FieldName').value
        }

        if (attribute == 'platformLongName') {
            PlatformName = this.searchForm.get('PlatformName').value
        }

        if (attribute == 'wellLongName') {
            WellName = this.searchForm.get('WellName').value
        }

        if (attribute == 'stringLongName') {
            StringName = this.searchForm.get('StringName').value
        }

        const url = composeUrl('/v1/WellHeaderInformation/FilterData', {
            field: FieldName,
            platform: PlatformName,
            well: WellName,
            wellstring: StringName,
        });

        return this._httpClient.get<Response<PaginateResponse<any>>>(url);
    }

    onSelected(type: string, attr: string, value: any) {
        const result = this.options[type].filter((row: any) => {
            return row[attr].toLowerCase() === value.toLowerCase()
        });

        if (result.length != 1) return;

        switch (type) {
            case 'fields':
                this.searchForm.patchValue({ Field: result[0] });
                this.fieldResult.emit(result[0]);
                break;
            case 'platforms':
                this.searchForm.patchValue({ Platform: result[0] });
                this.platformResult.emit(result[0]);
                break;
            case 'wells':
                this.searchForm.patchValue({ Well: result[0] });
                this.wellResult.emit(result[0]);
                break;
            case 'wellStrings':
                this.searchForm.patchValue({ String: result[0] });
                this.stringResult.emit(result[0]);
                break;
        }
    }

    onSearch(event?: Event) {
        // emit the form value or call internal service
        this.searchTriggered.emit(this.searchForm.value);
      }
}
